import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { io } from "socket.io-client";
import MapComponent from "../components/MapComponent";

const isSharedView = window.location.pathname.startsWith("/m/");

const MissionView = () => {
  const { id: missionId } = useParams();

  const socketRef = useRef(null);

  const [missionData, setMissionData] = useState(null);
  const [error, setError] = useState(null);

  const [livePosition, setLivePosition] = useState(null);
  const [liveHazards, setLiveHazards] = useState([]);
  const [dispatchNotes, setDispatchNotes] = useState([]);

  // 🧭 Mission-authoritative state
  const [routes, setRoutes] = useState([]);
  const [pireps, setPireps] = useState([]);

  const [isFlightActive, setIsFlightActive] = useState(false);

  // =====================================================
  // SOCKET SETUP + MISSION SYNC
  // =====================================================
  useEffect(() => {
    if (!missionId) return;

    // 🔹 Create socket ONCE
    if (!socketRef.current) {
      socketRef.current = io("https://ficon-x.com");
    }

    const socket = socketRef.current;

    console.log("📡 Joining mission room:", missionId);
    socket.emit("join_mission_room", missionId);

    fetch(`https://ficon-x.com/api/api/mission/${missionId}`)
      .then(async (res) => {
        const text = await res.text();

        console.log("🧪 Mission fetch URL:", res.url);
        console.log("🧪 Mission status:", res.status);
        console.log("🧪 Mission raw response (first 300 chars):", text.slice(0, 300));

        if (!res.ok) {
          throw new Error("Mission not found.");
        }

        try {
          return JSON.parse(text);
        } catch (e) {
          console.error("❌ Mission JSON parse failed");
          console.error("❌ FULL RESPONSE:", text);
          throw e;
        }
      })
      .then((data) => {
        setMissionData(data);
      })
      .catch((err) => {
        console.error("Mission fetch error:", err);
        setError("Mission not found or expired.");
      });


    // 🔹 Live feeds
    socket.on("position_update", setLivePosition);

    socket.on("hazard_update", (haz) => {
      setLiveHazards((prev) => [...prev, haz]);
    });

    socket.on("dispatch_note", (note) => {
      setDispatchNotes((prev) => [...prev, note]);
    });

    // 🔹 PRIMARY SYNC EVENT
    socket.on("mission_update", (data) => {
      console.log("🔄 mission_update:", data);

      if (data.routes) setRoutes(data.routes);
      if (data.pireps) setPireps(data.pireps);

      if (data.map_view || data.routes || data.pireps) {
        setMissionData((prev) => {
          if (!prev) return prev;

          const oldBriefing = prev.briefing || {};

          return {
            ...prev,
            briefing: {
              ...oldBriefing,
              ...(data.routes && { route: data.routes }),
              ...(data.map_view && { mapView: data.map_view }),
            },
          };
        });
      }

      if (data.position) setLivePosition(data.position);
      if (data.weather) setLiveHazards((prev) => [...prev, ...data.weather]);
      if (data.notes) setDispatchNotes((prev) => [...prev, ...data.notes]);
    });

    return () => {
      console.log("🔌 Leaving mission:", missionId);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [missionId]);

  // =====================================================
  // CAPTAIN-ONLY: PROMOTE LOCAL ROUTE INTO MISSION
  // =====================================================
  useEffect(() => {
    if (!missionId) return;
    if (!missionData) return;

    const isCaptain =
      new URLSearchParams(window.location.search).get("host") === "true";
    if (!isCaptain) return;

    const raw = localStorage.getItem("activeFlight");
    if (!raw) return;

    let flight;
    try {
      flight = JSON.parse(raw);
    } catch {
      return;
    }

    if (!flight?.route) return;

    console.log("🧭 Captain promoting route to mission");

    socketRef.current.emit("confirm_route", {
      mission_id: missionId,
      route: flight.route,
    });
  }, [missionId, missionData]);

  // =====================================================
  // UI STATES
  // =====================================================
  if (error) {
    return (
      <div className="p-4 text-center text-red-500 font-medium">
        {error}
      </div>
    );
  }

  if (!missionData) {
    return (
      <div className="p-4 text-center text-gray-400">
        Loading mission briefing…
      </div>
    );
  }

  const coreData = missionData.briefing || missionData.data || {};
  const {
    origin,
    destination,
    route,
    altitude,
    tas,
    totalFuel,
    timeEnroute,
    mapView,
  } = coreData;

  useEffect(() => {
    const raw = localStorage.getItem("activeFlight");
    if (!raw) return;

    try {
      const flight = JSON.parse(raw);
      if (flight?.route) {
        setIsFlightActive(true);
      }
    } catch {}
  }, []);

  if (!origin || !destination) {
    return (
      <div className="p-4 text-center text-gray-400">
        Initializing mission…
      </div>
    );
  }

  // =====================================================
  // RENDER
  // =====================================================
  return (
    <div className="w-full h-screen relative">
      {/* 🧭 Mission Header */}
      <div className="absolute top-0 left-0 w-full bg-black bg-opacity-50 text-white p-4 z-10 text-sm flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <strong>🛩️ {origin} ➝ {destination}</strong><br />
          Route: {route}<br />
          Altitude: {altitude} ft | TAS: {tas} kt | Fuel: {totalFuel} | ETE: {timeEnroute}
        </div>
        <div className="mt-2 sm:mt-0">
          Mission ID: <span className="font-mono">{missionData.mission_id}</span>
        </div>
      </div>

      {/* 🗺️ MAP */}
      <MapComponent
        missionData={coreData}
        mapView={mapView}
        livePosition={livePosition}
        hazards={liveHazards}
        notes={dispatchNotes}
        routes={routes}
        pireps={pireps}
        readOnly={isSharedView}
        isFlightActive={isFlightActive}
        setIsFlightActive={setIsFlightActive}
      />
    </div>
  );
};

export default MissionView;
